import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { HoldAuditType } from '@planning/app/api'
import { getHoldAuditEventLabel } from '@planning/components/hold/hold.utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { formatDateTime } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { HoldsViewStore } from '../stores/HoldsViewStore'

interface Props {
  holdId: number
  viewStore: HoldsViewStore
}

export const HoldsAuditDetailPanel = observer(({ holdId, viewStore }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  useEffect(() => {
    viewStore.fetchAuditTrail(holdId)
  }, [holdId, viewStore])

  const entry = viewStore.auditTrailByHoldId.get(holdId)

  const renderField = (label: string, value?: string | null) => (
    <Stack minWidth={180} p={2}>
      <Typography variant='subtitle2'>{label}</Typography>
      <Typography variant='body2'>{value ?? '—'}</Typography>
    </Stack>
  )

  const renderContent = () => {
    if (!entry || entry === 'loading') {
      return (
        <Stack justifyContent='center' alignItems='center' p={3}>
          <CircularProgress size={20} />
        </Stack>
      )
    }

    if (!entry.events?.length) {
      return (
        <Stack p={3}>
          <Typography variant='body2' color='text.secondary'>
            {t('holds.auditTrail.noEvents', 'No audit events recorded')}
          </Typography>
        </Stack>
      )
    }

    return (
      <Stack direction='column' divider={<Divider />}>
        {entry.events.map((event, i) => {
          const { on, by } = getHoldAuditEventLabel(event.type)
          return (
            <Stack key={`${event.at}-${i}`} direction='row' flexWrap='wrap'>
              {renderField(on, formatDateTime(event.at))}
              {renderField(by, event.responsibleUserName)}
              {event.type === HoldAuditType.Override &&
                renderField(t('overrideType', 'Override type'), event.overrideType)}
              {renderField(t('description', 'Description'), event.reason)}
            </Stack>
          )
        })}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack direction='row'>
        <Stack
          direction='row'
          p={3}
          sx={{
            left: 0,
            position: 'sticky',
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.grey[100],
          }}
        >
          <Typography variant='subtitle2' fontWeight={600}>
            {t('auditTrail', 'Audit trail')}
          </Typography>
        </Stack>

        {renderContent()}
      </Stack>
    </Box>
  )
})
